import {
  columnFilteringFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_arrIncludesSome,
  filterFn_includesString,
  globalFilteringFeature,
  metaHelper,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
  type RowData,
  type TableOptions,
} from '@tanstack/react-table';

/**
 * Дополнительные сведения о колонке, которых нет в самой таблице.
 *
 * Таблица знает, как достать значение, но не знает, что число прижимают
 * вправо, а столбец действий не тянется. Это оформление, и живёт оно здесь.
 */
export interface DataTableColumnMeta {
  /** Куда прижимать содержимое. Числа и действия — вправо. */
  align?: 'start' | 'end';
  /** Ширина колонки: `'40px'`, `'22%'`. Остальные делят остаток. */
  width?: string;
  /**
   * Название колонки словами — для переключателя видимости.
   *
   * Заголовок бывает разметкой, а не строкой: значок, подсказка, счётчик.
   * В списке колонок такое не показать.
   */
  name?: string;
}

/**
 * Один набор возможностей на все таблицы Faceta.
 *
 * В девятой версии таблица собирается из отдельных частей, и набор входит в
 * её тип: API сортировки не существует, пока не подключена сортировка. Это
 * означает, что общий компонент отображения не может принимать «любую
 * таблицу» — он принимает таблицу с этим набором.
 *
 * Так и надо: списки тенантов, сайтов, пользователей и записей ведут себя
 * одинаково, и различаться они должны колонками, а не тем, где сортировка
 * есть, а где её забыли подключить.
 */
export const dataTableFeatures = tableFeatures({
  columnVisibilityFeature,

  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  filterFns: {
    includesString: filterFn_includesString,
    arrIncludesSome: filterFn_arrIncludesSome,
  },

  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    text: sortFn_text,
    datetime: sortFn_datetime,
  },

  rowSelectionFeature,

  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),

  columnMeta: metaHelper<DataTableColumnMeta>(),
});

export type DataTableFeatures = typeof dataTableFeatures;

/**
 * Помощник для описания колонок.
 *
 * Вызывается один раз на модуль, вне компонента: колонки обязаны быть
 * постоянными, иначе таблица пересчитывает модель на каждой перерисовке.
 */
export function dataTableColumns<TData extends RowData>() {
  return createColumnHelper<DataTableFeatures, TData>();
}

/** Таблица с набором Faceta. Состояние — на стороне вызывающего кода. */
export function useDataTable<TData extends RowData>(
  options: Omit<TableOptions<DataTableFeatures, TData>, 'features'>,
) {
  return useTable<DataTableFeatures, TData>({ features: dataTableFeatures, ...options });
}
